// Shared, session-cached loaders and selectors for the public storefront
// (TopNav + HomePage). The sessionStorage keys match the ones CategoryPage
// reads, and concurrent callers share one in-flight request, so a first visit
// fetches each collection once instead of once per component.
import { api } from './apiClient';

export const STOREFRONT_CACHE_KEYS = {
  homepageAssets: 'homepage_assets',
  categories: 'homepage_categories',
  events: 'storefront_events',
  homepageCopy: 'storefront_homepage_copy',
};

const inFlight = new Map();

const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable (private mode) — the data still works uncached.
  }
};

const loadOnce = (key, fetcher) => {
  const cached = readCache(key);
  if (cached) return Promise.resolve(cached);
  if (!inFlight.has(key)) {
    const request = fetcher()
      .then((data) => {
        writeCache(key, data);
        return data;
      })
      .finally(() => inFlight.delete(key));
    inFlight.set(key, request);
  }
  return inFlight.get(key);
};

export const loadHomepageAssets = () =>
  loadOnce(STOREFRONT_CACHE_KEYS.homepageAssets, () => api.homepage.getAssets());

export const loadCategories = () =>
  loadOnce(STOREFRONT_CACHE_KEYS.categories, () => api.categories.list({ is_active: true }));

export const loadEvents = () =>
  loadOnce(STOREFRONT_CACHE_KEYS.events, () => api.events.list());

// Admin wording overrides for the homepage (Admin → Homepage Text). Resolves
// to {} when there are none, or when the API doesn't serve site content yet,
// so the storefront always falls back to its default copy.
export const loadHomepageCopy = () =>
  loadOnce(STOREFRONT_CACHE_KEYS.homepageCopy, () =>
    api.siteContent.get('homepage').then((doc) => doc?.values || {}, () => ({})));

// Drops cached storefront data (by STOREFRONT_CACHE_KEYS name) so an admin's
// changes show on the next page view without starting a new session.
export const clearStorefrontCache = (...names) => {
  names.forEach((name) => {
    try {
      sessionStorage.removeItem(STOREFRONT_CACHE_KEYS[name]);
    } catch {
      // Storage unavailable — nothing was cached.
    }
  });
};

export const clearHomepageCopyCache = () => clearStorefrontCache('homepageCopy');

// ---------- Homepage assets ----------

const bySortOrder = (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0);

export const selectHomepageContent = (assets = []) => {
  const active = assets.filter((asset) => asset.is_active);
  return {
    hero: active.find((asset) => asset.asset_type === 'hero_section') || null,
    featuredProducts: active.filter((asset) => asset.asset_type === 'featured_product').sort(bySortOrder),
    featuredCategories: active.filter((asset) => asset.asset_type === 'featured_category').sort(bySortOrder),
  };
};

export const productImages = (item) => {
  if (item?.images?.length) return item.images;
  return item?.image_url ? [item.image_url] : [];
};

// ---------- Categories ----------

export const categoryPath = (categoryId) => `/category/${categoryId}`;

// Pulls the category id out of an admin-configured link like "/category/<id>".
export const categoryIdFromLink = (link) => {
  const match = /\/category\/([^/?#]+)/.exec(link || '');
  return match ? match[1] : null;
};

const byOrderThenName = (a, b) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name);

// Builds the hierarchy from the flat active-category list (no extra /tree
// request). Top-level collections follow the admin's featured order first,
// then the rest alphabetically. Each node knows its top-level `rootId`.
export const buildCategoryTree = (categories = [], featuredCategories = []) => {
  const byId = new Map(categories.map((category) => [category.id, { ...category, children: [] }]));
  const roots = [];

  byId.forEach((node) => {
    const parent = node.parent_category_id ? byId.get(node.parent_category_id) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });

  byId.forEach((node) => node.children.sort(byOrderThenName));

  const featuredRank = new Map(
    featuredCategories.map((asset, index) => [categoryIdFromLink(asset.link_url), index]),
  );
  const rank = (node) => (featuredRank.has(node.id) ? featuredRank.get(node.id) : Infinity);
  roots.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));

  const assignRoot = (node, rootId) => {
    node.rootId = rootId;
    node.children.forEach((child) => assignRoot(child, rootId));
  };
  roots.forEach((root) => assignRoot(root, root.id));

  return { roots, byId };
};

const FALLBACK_COLLECTIONS = 9;

// The collections the homepage shows (marquee + mosaic): the admin's featured
// collections in order, enriched with their sub-categories. With none
// featured, the first top-level collections stand in.
export const selectFeaturedCollections = (featuredCategories, tree) => {
  const featured = featuredCategories.map((asset) => {
    const node = tree.byId.get(categoryIdFromLink(asset.link_url));
    return {
      id: asset.id,
      title: asset.title || node?.name || 'Collection',
      image: asset.image_url || node?.image_url,
      to: asset.link_url || (node ? categoryPath(node.id) : '/products'),
      children: node?.children || [],
    };
  });
  if (featured.length) return featured;
  return tree.roots.slice(0, FALLBACK_COLLECTIONS).map((node) => ({
    id: node.id,
    title: node.name,
    image: node.image_url,
    to: categoryPath(node.id),
    children: node.children,
  }));
};

// Every category with its breadcrumb trail, for client-side search.
export const flattenCategoryTree = (roots = []) => {
  const entries = [];
  const walk = (nodes, trail) => {
    nodes.forEach((node) => {
      entries.push({ id: node.id, name: node.name, trail, childCount: node.children.length });
      walk(node.children, [...trail, node.name]);
    });
  };
  walk(roots, []);
  return entries;
};

// ---------- Events ----------

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Admin-entered dates look like "10/17/2026 - 10/21/2026" or "10/17/2026".
export const parseEventDates = (value) => {
  const dates = (String(value || '').match(/\d{1,2}\/\d{1,2}\/\d{2,4}/g) || [])
    .map((part) => {
      const [month, day, year] = part.split('/').map(Number);
      return new Date(year < 100 ? 2000 + year : year, month - 1, day);
    })
    .filter((date) => !Number.isNaN(date.getTime()));
  if (!dates.length) return null;
  return { start: dates[0], end: dates[dates.length - 1] };
};

// Active events that haven't ended yet, soonest first. Events whose date
// can't be parsed are kept (we can't tell they're over) and sorted last.
export const upcomingEvents = (events = [], now = new Date()) => {
  const today = startOfDay(now);
  return events
    .map((event) => ({ ...event, dates: parseEventDates(event.event_date) }))
    .filter((event) => !event.dates || event.dates.end >= today)
    .sort((a, b) => (a.dates ? a.dates.start.getTime() : Infinity) - (b.dates ? b.dates.start.getTime() : Infinity));
};

const shortMonth = (date) => date.toLocaleString('en-US', { month: 'short' });

export const formatEventRange = ({ start, end }) => {
  const year = end.getFullYear();
  if (start.getTime() === end.getTime()) {
    return `${shortMonth(start)} ${start.getDate()}, ${year}`;
  }
  if (start.getFullYear() !== year) {
    return `${shortMonth(start)} ${start.getDate()}, ${start.getFullYear()} – ${shortMonth(end)} ${end.getDate()}, ${year}`;
  }
  if (start.getMonth() === end.getMonth()) {
    return `${shortMonth(start)} ${start.getDate()} – ${end.getDate()}, ${year}`;
  }
  return `${shortMonth(start)} ${start.getDate()} – ${shortMonth(end)} ${end.getDate()}, ${year}`;
};

export const daysUntil = (date, now = new Date()) =>
  Math.round((startOfDay(date) - startOfDay(now)) / 86400000);

// ---------- Trade account state ----------

// Only approval state, never the pricing tier: customers must not be able
// to tell which tier they're on, so the storefront never receives it here.
export const tradeStatusFor = ({ isAuthenticated, isApproved, isAdmin }) => {
  if (!isAuthenticated) return { state: 'guest' };
  if (isApproved || isAdmin) return { state: 'active' };
  return { state: 'pending' };
};

export const firstNameOf = (profile) => profile?.name?.trim().split(/\s+/)[0] || '';
